import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';

// Use relative URL to avoid double /api/ issue
const API_BASE = '/api/licenses/public';

const PACKS = [
  { id: 'small', name: 'Small Pack', credits: 500, price: '$9', priceCents: 900, perCredit: '$0.018', color: '#6366f1', popular: false },
  { id: 'medium', name: 'Medium Pack', credits: 2000, price: '$25', priceCents: 2500, perCredit: '$0.0125', color: '#8b5cf6', popular: true },
  { id: 'large', name: 'Large Pack', credits: 5000, price: '$49', priceCents: 4900, perCredit: '$0.0098', color: '#a855f7', popular: false },
];

export default function BuyCredits() {
  const [searchParams] = useSearchParams();
  const licenseId = searchParams.get('license_id') || '';
  const [manualLicenseId, setManualLicenseId] = useState(licenseId);
  const [selectedPack, setSelectedPack] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const effectiveLicenseId = licenseId || manualLicenseId;

  // Load Razorpay script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    return () => { try { document.body.removeChild(script); } catch(e) {} };
  }, []);

  const handlePurchase = async (pack) => {
    if (!effectiveLicenseId) {
      setError('Please enter your License ID first.');
      return;
    }

    setLoading(true);
    setError('');
    setSelectedPack(pack.id);

    try {
      const orderRes = await fetch(`${API_BASE}/create-credit-order/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pack_id: pack.id, license_id: effectiveLicenseId }),
      });

      const orderData = await orderRes.json();
      if (!orderRes.ok) throw new Error(orderData.error || 'Failed to create order');

      const options = {
        key: orderData.key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'KrossIQ',
        description: `${pack.credits} AI Credits`,
        order_id: orderData.order_id,
        handler: async (response) => {
          try {
            const verifyRes = await fetch(`${API_BASE}/verify-credit-payment/`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                license_id: effectiveLicenseId,
                pack_id: pack.id,
              }),
            });
            const verifyData = await verifyRes.json();
            if (verifyRes.ok && verifyData.success) {
              setResult({ success: true, credits: verifyData.credits_added, balance: verifyData.new_balance, paymentId: verifyData.payment_id });
            } else {
              setError(verifyData.error || 'Payment verification failed');
            }
          } catch (e) {
            setError('Payment verification failed. Please contact support@krossiq.com');
          }
          setLoading(false);
        },
        prefill: { email: orderData.customer_email || '', name: orderData.customer_name || '' },
        theme: { color: '#6366f1' },
        modal: { ondismiss: () => { setLoading(false); setSelectedPack(null); } },
      };

      if (window.Razorpay) {
        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', (resp) => {
          setError(resp.error?.description || 'Payment failed');
          setLoading(false);
          setSelectedPack(null);
        });
        rzp.open();
      } else {
        setError('Payment gateway not loaded. Please refresh the page.');
        setLoading(false);
      }
    } catch (e) {
      setError(e.message || 'Something went wrong');
      setLoading(false);
      setSelectedPack(null);
    }
  };

  // Success screen
  if (result?.success) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={styles.successCard}>
            <div style={{ fontSize: '64px', marginBottom: '16px' }}>&#10003;</div>
            <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#059669', marginBottom: '8px' }}>Payment Successful!</h1>
            <p style={{ fontSize: '18px', color: '#475569', marginBottom: '24px' }}>
              <strong>{result.credits.toLocaleString()}</strong> credits have been added to your account.
            </p>
            <div style={styles.balanceBox}>
              <span style={{ color: '#64748b', fontSize: '14px' }}>New Balance</span>
              <span style={{ fontSize: '32px', fontWeight: '700', color: '#6366f1' }}>{result.balance.toLocaleString()}</span>
              <span style={{ color: '#64748b', fontSize: '13px' }}>credits</span>
            </div>
            <p style={{ fontSize: '13px', color: '#94a3b8', marginTop: '16px' }}>Payment ID: {result.paymentId}</p>
            <p style={{ fontSize: '14px', color: '#475569', marginTop: '24px' }}>
              Credits will sync automatically in your KrossIQ app. You can close this page.
            </p>
            <Link to="/" style={styles.backLink}>Back to KrossIQ.com</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <Link to="/" style={{ textDecoration: 'none' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#6366f1', marginBottom: '4px' }}>KrossIQ</h2>
          </Link>
          <h1 style={{ fontSize: '32px', fontWeight: '700', color: '#1e293b', marginBottom: '8px' }}>Buy AI Credits</h1>
          <p style={{ fontSize: '16px', color: '#64748b' }}>Top up your credits to continue using AI-powered features</p>
        </div>

        {!licenseId && (
          <div style={styles.licenseInput}>
            <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '6px' }}>License ID</label>
            <input type="text" value={manualLicenseId} onChange={(e) => setManualLicenseId(e.target.value)}
              placeholder="Enter your License ID from KrossIQ app" style={styles.input} />
            <p style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
              Find your License ID in KrossIQ app &rarr; User Profile &rarr; License Details
            </p>
          </div>
        )}

        {error && <div style={styles.errorBox}>{error}</div>}

        <div style={styles.packsGrid}>
          {PACKS.map((pack) => (
            <div key={pack.id} style={{ ...styles.packCard, borderColor: pack.popular ? '#6366f1' : '#e2e8f0', transform: pack.popular ? 'scale(1.02)' : 'none' }}>
              {pack.popular && <div style={styles.popularBadge}>Most Popular</div>}
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b', marginBottom: '4px' }}>{pack.name}</h3>
              <div style={{ fontSize: '40px', fontWeight: '700', color: pack.color, marginBottom: '4px' }}>{pack.price}</div>
              <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '16px' }}>{pack.credits.toLocaleString()} credits</div>
              <div style={{ fontSize: '13px', color: '#94a3b8', marginBottom: '20px' }}>{pack.perCredit} per credit</div>
              <button
                onClick={() => handlePurchase(pack)}
                disabled={loading}
                style={{ ...styles.buyButton, backgroundColor: pack.color, opacity: loading && selectedPack !== pack.id ? 0.5 : 1 }}
              >
                {loading && selectedPack === pack.id ? 'Processing...' : `Buy ${pack.credits.toLocaleString()} Credits`}
              </button>
            </div>
          ))}
        </div>

        <div style={styles.infoSection}>
          <div style={styles.infoItem}><strong>Instant delivery</strong> — Credits added immediately after payment</div>
          <div style={styles.infoItem}><strong>Credits never expire</strong> — Use at your own pace</div>
          <div style={styles.infoItem}><strong>Secure payment</strong> — Powered by Razorpay</div>
        </div>

        <div style={{ textAlign: 'center', marginTop: '24px', padding: '16px', background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
          <p style={{ fontSize: '14px', color: '#475569', margin: 0 }}>
            Need bulk credits? <a href="mailto:support@krossiq.com?subject=Bulk%20Credits%20Inquiry" style={{ color: '#6366f1', fontWeight: '600', textDecoration: 'none' }}>Contact support@krossiq.com</a> for custom pricing.
          </p>
        </div>

        <div style={{ textAlign: 'center', marginTop: '32px' }}>
          <Link to="/" style={styles.backLink}>Back to KrossIQ.com</Link>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 50%, #f8fafc 100%)', padding: '40px 20px' },
  container: { maxWidth: '900px', margin: '0 auto' },
  licenseInput: { maxWidth: '500px', margin: '0 auto 32px', padding: '20px', background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0' },
  input: { width: '100%', padding: '10px 14px', fontSize: '14px', border: '1px solid #d1d5db', borderRadius: '8px', outline: 'none', boxSizing: 'border-box' },
  errorBox: { padding: '12px 16px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', fontSize: '14px', textAlign: 'center', maxWidth: '500px', margin: '0 auto 24px' },
  packsGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px', marginBottom: '40px' },
  packCard: { background: '#fff', borderRadius: '16px', border: '2px solid #e2e8f0', padding: '32px 24px', textAlign: 'center', position: 'relative', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
  popularBadge: { position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)', backgroundColor: '#6366f1', color: '#fff', padding: '4px 16px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' },
  buyButton: { width: '100%', padding: '12px', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  successCard: { background: '#fff', borderRadius: '16px', border: '2px solid #d1fae5', padding: '48px', textAlign: 'center', maxWidth: '500px', margin: '0 auto', boxShadow: '0 4px 24px rgba(0,0,0,0.06)' },
  balanceBox: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', padding: '20px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' },
  infoSection: { display: 'flex', justifyContent: 'center', gap: '32px', flexWrap: 'wrap' },
  infoItem: { fontSize: '13px', color: '#64748b' },
  backLink: { color: '#6366f1', textDecoration: 'none', fontSize: '14px', fontWeight: '500' },
};
